/**
 * Arranque de la aplicacion.
 *
 * Mantiene el estado (arbol, indice, disposicion, escena) y se encarga de que
 * todo se vuelva a calcular en el orden correcto cuando los datos cambian:
 *
 *     arbol  ->  indice  ->  generaciones  ->  orden  ->  fisica  ->  escena
 *
 * Cada paso depende del anterior, asi que no se pueden recalcular por
 * separado. Por eso hay un solo sitio donde se hace, `recargar()`, y todo lo
 * demas lo llama.
 */
import ejemploFicticio from "../test/fixtures/ficticio.gramps?raw";

import { Indice } from "./core/grafo.js";
import { leerGedcom, escribirGedcom } from "./core/gramps/gedcom.js";
import { leerGramps } from "./core/gramps/parse.js";
import { escribirGramps } from "./core/gramps/serialize.js";
import { borrarPersona } from "./core/editar.js";
import { nombreCompleto, type Arbol } from "./core/model.js";
import { validar, type Incidencia } from "./core/validar.js";
import { disponer, type Disposicion } from "./layout/layout.js";
import { pantallaAcceso } from "./nube/acceso.js";
import { ErrorNube, Nube } from "./nube/cliente.js";
import { ESCALA_MAXIMA, ESCALA_MINIMA } from "./render/camara.js";
import { Escena, type Metricas } from "./render/escena.js";
import { buscar, el, vaciar } from "./ui/dom.js";
import { formularioPersona, formularioRelaciones, Modal } from "./ui/formularios.js";
import {
  etiquetaBusqueda,
  pintarFicha,
  pintarLeyenda,
  pintarParentesco,
  pintarResumen,
  pintarRevision,
  type Contexto,
} from "./ui/paneles.js";

/**
 * Puente con Electron, si la aplicacion corre como escritorio.
 * En el navegador no existe y se usan la descarga y el selector de archivos.
 */
interface Puente {
  abrir(): Promise<{ ruta: string; contenido: string } | null>;
  guardar(
    sugerencia: string,
    contenido: string,
    filtro: "gramps" | "ged",
  ): Promise<string | null>;
  argumentoInicial(): Promise<{ ruta: string; contenido: string } | null>;
  /** Avisos del menu de la ventana (abrir, guardar). */
  alMenu?(fn: (accion: string) => void): void;
}

declare global {
  interface Window {
    puente?: Puente;
  }
}

class Aplicacion {
  private arbol!: Arbol;
  private ix!: Indice;
  private disposicion!: Disposicion;
  private escena!: Escena;
  private incidencias: Incidencia[] = [];

  private seleccion: string | undefined;
  private parentescoA: string | undefined;
  private parentescoB: string | undefined;
  private perspectiva: string | undefined;
  private nombreArchivo = "ejemplo ficticio";
  private pestana = "ficha";

  private readonly modal = new Modal();

  /**
   * Almacen en la nube, si la aplicacion viene servida desde la web.
   * En el escritorio queda sin definir y todo va contra el disco.
   */
  private nube?: Nube;
  /** Version del arbol sobre la que se esta editando, para no pisar a nadie. */
  private shaRemoto: string | null = null;
  private sinGuardar = false;

  async iniciar(): Promise<void> {
    // Arranca con los datos FICTICIOS que vienen con el programa, para que al
    // abrirlo haya algo que mirar sin tener que cargar nada. Los datos reales
    // se abren con el boton.
    const { arbol } = leerGramps(ejemploFicticio);
    this.arbol = arbol;
    this.ix = new Indice(arbol);
    this.disposicion = disponer(this.ix, (h) => this.nombre(h));
    this.incidencias = validar(this.ix);

    this.escena = await Escena.crear({
      contenedor: buscar("#lienzo"),
      arbol: this.arbol,
      ix: this.ix,
      disposicion: this.disposicion,
      alSeleccionar: (h) => this.seleccionar(h),
      alFondo: () => {
        this.seleccion = undefined;
        this.refrescar();
      },
      alMedir: (m) => this.pintarMetricas(m),
    });
    this.escena.encuadrarTodo(true);

    pintarLeyenda(buscar("#leyenda"));
    this.pintarPerspectiva();
    this.conectarBarra();
    this.conectarNube();
    this.conectarPestanas();

    // El menu de la ventana de Electron dispara las mismas acciones que los
    // botones, para no tener dos caminos distintos que mantener.
    window.puente?.alMenu?.((accion) => {
      if (accion === "abrir") buscar<HTMLButtonElement>("#btn-abrir").click();
      else if (accion === "guardar") void this.exportar("gramps");
    });
    this.refrescar();
    buscar("#aviso-carga").classList.add("oculto");

    // Si Electron arranco con un fichero como argumento, se abre.
    const inicial = await window.puente?.argumentoInicial?.();
    if (inicial) this.cargarTexto(inicial.contenido, inicial.ruta);

    // Y si esto viene servido desde la web, se pide la clave y se trae el
    // arbol de verdad. Mientras tanto se ve el de ejemplo por detras, que es
    // mas agradable que una pantalla en negro.
    await this.arrancarNube();
  }

  private nombre(h: string): string {
    const p = this.arbol.personas.get(h);
    return p ? `${nombreCompleto(p)} [${p.id ?? h}]` : h;
  }

  // --- recalculo ----------------------------------------------------------

  /**
   * Vuelve a calcular todo lo que depende del arbol y repinta.
   *
   * Es deliberadamente completo: con 80 personas cuesta unas decenas de
   * milisegundos, y hacerlo entero evita la clase de error en el que una
   * parte del estado se queda vieja.
   */
  private recargar(): void {
    if (this.nube?.puedeEditar) {
      this.sinGuardar = true;
      this.estadoNube("Sin guardar");
    }
    this.ix = new Indice(this.arbol);
    this.disposicion = disponer(this.ix, (h) => this.nombre(h));
    this.incidencias = validar(this.ix);
    this.escena.cargar(this.arbol, this.ix, this.disposicion);
    if (this.seleccion && !this.arbol.personas.has(this.seleccion)) {
      this.seleccion = undefined;
    }
    for (const k of ["parentescoA", "parentescoB", "perspectiva"] as const) {
      const v = this[k];
      if (v && !this.arbol.personas.has(v)) this[k] = undefined;
    }
    this.pintarPerspectiva();
    this.refrescar();
  }

  /**
   * Carga un arbol desde texto, detectando si es Gramps XML o GEDCOM.
   * Es publico porque lo usan el puente de Electron y las pruebas manuales
   * desde la consola (`window.arbolApp`).
   */
  cargarTexto(texto: string, nombre: string): void {
    try {
      const esGedcom = /^\s*0\s+HEAD/m.test(texto) && !texto.includes("<database");
      let avisos: string[] = [];
      if (esGedcom) {
        const r = leerGedcom(texto);
        this.arbol = r.arbol;
        avisos = r.avisos;
      } else {
        const r = leerGramps(texto);
        this.arbol = r.arbol;
        avisos = r.avisos.map((a) => (a.objeto ? `[${a.objeto}] ${a.mensaje}` : a.mensaje));
      }
      this.nombreArchivo = nombre;
      this.seleccion = undefined;
      this.parentescoA = undefined;
      this.parentescoB = undefined;
      this.recargar();
      this.escena.encuadrarTodo();
      if (avisos.length) this.mostrarAvisos("Importación", avisos);
    } catch (e) {
      this.mostrarAvisos("No se pudo leer el archivo", [
        e instanceof Error ? e.message : String(e),
      ]);
    }
  }

  private mostrarAvisos(titulo: string, mensajes: string[]): void {
    const caja = el("div", { clase: "campos" });
    for (const m of mensajes) {
      caja.appendChild(el("div", { clase: "incidencia aviso", texto: m }));
    }
    const ok = el("button", { clase: "primario", texto: "Entendido" });
    ok.addEventListener("click", () => this.modal.cerrar());
    caja.appendChild(el("div", { clase: "pie-modal", hijos: [ok] }));
    this.modal.abrir(titulo, caja);
  }


  // --- nube ---------------------------------------------------------------

  /**
   * Averigua si hay almacen en la nube detras y, si lo hay, pide la clave y
   * trae el arbol. Si no lo hay no pasa nada: la aplicacion sigue funcionando
   * contra archivos del disco, que es como va en el escritorio.
   */
  private async arrancarNube(): Promise<void> {
    const nube = await Nube.detectar();
    if (!nube) return;
    this.nube = nube;
    buscar("#grupo-nube").classList.remove("oculto");

    if (!nube.haySesion) {
      this.pedirClave();
      return;
    }
    await this.traerDeLaNube();
  }

  private pedirClave(): void {
    const nube = this.nube;
    if (!nube) return;
    const pantalla = pantallaAcceso({
      nube,
      alEntrar: () => {
        pantalla.remove();
        void this.traerDeLaNube();
      },
    });
    document.body.appendChild(pantalla);
  }

  /** Trae el arbol guardado y lo pone en pantalla. */
  private async traerDeLaNube(): Promise<void> {
    const nube = this.nube;
    if (!nube) return;
    this.estadoNube("Cargando…");
    try {
      const r = await nube.cargar();
      if (r.texto) {
        this.cargarTexto(r.texto, "árbol en la nube");
        this.shaRemoto = r.sha;
        this.sinGuardar = false;
      } else {
        // Todavia no hay nada guardado: se queda el ejemplo y se avisa de
        // que lo primero que guarde creara el archivo.
        this.shaRemoto = null;
        this.nombreArchivo = "árbol en la nube (vacío)";
      }
      this.aplicarRol();
      this.estadoNube(nube.puedeEditar ? "Al día" : "Solo lectura", "guardado");
    } catch (e) {
      if (e instanceof ErrorNube && e.esSesion) {
        this.pedirClave();
        return;
      }
      this.estadoNube("No se pudo cargar", "error");
      this.mostrarAvisos("No se pudo cargar el árbol", [
        e instanceof Error ? e.message : String(e),
      ]);
    }
  }

  /** Sube el arbol tal como esta ahora. */
  private async guardarEnLaNube(): Promise<void> {
    const nube = this.nube;
    if (!nube) return;
    if (!nube.puedeEditar) {
      this.mostrarAvisos("Solo lectura", [
        "Tu clave permite mirar el árbol, pero no cambiarlo.",
      ]);
      return;
    }

    this.estadoNube("Guardando…", "guardando");
    try {
      const sha = await nube.guardar(
        escribirGramps(this.arbol),
        this.shaRemoto,
        `${this.arbol.personas.size} personas, ${this.arbol.familias.size} familias`,
      );
      this.shaRemoto = sha;
      this.sinGuardar = false;
      this.estadoNube("Guardado", "guardado");
    } catch (e) {
      if (e instanceof ErrorNube && e.esConflicto) {
        this.estadoNube("Hay cambios más nuevos", "error");
        this.resolverConflicto();
        return;
      }
      if (e instanceof ErrorNube && e.esSesion) {
        this.estadoNube("Sesión caducada", "error");
        this.pedirClave();
        return;
      }
      this.estadoNube("No se pudo guardar", "error");
      this.mostrarAvisos("No se pudo guardar", [
        e instanceof Error ? e.message : String(e),
      ]);
    }
  }

  /**
   * Alguien guardo algo mientras tu editabas -- tu mismo desde otro
   * dispositivo, probablemente. Se pregunta en vez de decidir: machacar el
   * trabajo del otro en silencio seria lo peor que podria hacer esto.
   */
  private resolverConflicto(): void {
    const caja = el("div", { clase: "campos" });
    caja.appendChild(
      el("p", {
        texto:
          "El árbol guardado cambió después de que abrieras este. Si guardas " +
          "encima, se perderá ese otro cambio.",
      }),
    );
    caja.appendChild(
      el("p", {
        clase: "pista",
        texto:
          "Lo normal es traer la versión guardada y volver a hacer tus cambios. " +
          "Si estás seguro de que lo tuyo es lo bueno, puedes forzar; el " +
          "historial del repositorio conserva lo anterior de todas formas.",
      }),
    );

    const traer = el("button", { clase: "primario", texto: "Traer la versión guardada" });
    traer.addEventListener("click", () => {
      this.modal.cerrar();
      void this.traerDeLaNube();
    });
    const forzar = el("button", { clase: "peligro", texto: "Guardar encima" });
    forzar.addEventListener("click", () => {
      this.modal.cerrar();
      // Sin sha, GitHub acepta la escritura sin comprobar la version.
      this.shaRemoto = null;
      void this.guardarEnLaNube();
    });
    caja.appendChild(el("div", { clase: "pie-modal", hijos: [forzar, traer] }));
    this.modal.abrir("Hay cambios más nuevos", caja);
  }

  private estadoNube(texto: string, clase = ""): void {
    const n = buscar("#estado-nube");
    n.textContent = texto;
    n.className = clase;
  }

  /** Aplica el rol: con solo lectura se esconde todo lo que cambia datos. */
  private aplicarRol(): void {
    const puede = this.nube?.puedeEditar ?? true;
    buscar<HTMLButtonElement>("#btn-nueva").classList.toggle("oculto", !puede);
    buscar<HTMLButtonElement>("#btn-guardar-nube").classList.toggle("oculto", !puede);
    this.refrescar();
  }

  private conectarNube(): void {
    buscar("#btn-guardar-nube").addEventListener("click", () => {
      void this.guardarEnLaNube();
    });
    buscar("#btn-salir").addEventListener("click", () => {
      this.nube?.salir();
      location.reload();
    });

    // Aviso al cerrar la pestana con cambios sin guardar. Es lo unico que
    // separa un rato de trabajo de perderlo.
    window.addEventListener("beforeunload", (ev) => {
      if (!this.nube || !this.sinGuardar || !this.nube.puedeEditar) return;
      ev.preventDefault();
      ev.returnValue = "";
    });
  }

  // --- interfaz -----------------------------------------------------------


  private contexto(): Contexto {
    return {
      arbol: this.arbol,
      ix: this.ix,
      disposicion: this.disposicion,
      incidencias: this.incidencias,
      seleccion: this.seleccion,
      parentescoA: this.parentescoA,
      parentescoB: this.parentescoB,
      perspectiva: this.perspectiva,
      soloLectura: this.nube ? !this.nube.puedeEditar : false,
      nombreArchivo: this.nombreArchivo,
      seleccionar: (h) => this.seleccionar(h),
      irA: (h) => this.escena.irA(h),
      editarPersona: (h) =>
        formularioPersona({
          arbol: this.arbol,
          ix: this.ix,
          handle: h,
          modal: this.modal,
          alGuardar: (nuevo) => {
            this.seleccion = nuevo;
            this.recargar();
          },
        }),
      editarRelaciones: (h) =>
        formularioRelaciones({
          arbol: this.arbol,
          ix: this.ix,
          handle: h,
          modal: this.modal,
          alGuardar: () => this.recargar(),
        }),
      nuevaPersona: () =>
        formularioPersona({
          arbol: this.arbol,
          ix: this.ix,
          modal: this.modal,
          alGuardar: (nuevo) => {
            this.seleccion = nuevo;
            this.recargar();
            this.escena.irA(nuevo);
          },
        }),
      borrarPersona: (h) => this.confirmarBorrado(h),
      ponerPerspectiva: (h) => this.ponerPerspectiva(h),
      ponerParentesco: (a, b) => {
        this.parentescoA = a;
        this.parentescoB = b;
        this.pestana = "parentesco";
        this.activarPestana();
        this.refrescar();
      },
    };
  }

  private confirmarBorrado(handle: string): void {
    const p = this.arbol.personas.get(handle);
    if (!p) return;
    const caja = el("div", { clase: "campos" });
    caja.appendChild(
      el("p", {
        texto: `Se va a borrar a ${nombreCompleto(p)} y a quitarla de todas sus familias. Los demás no se tocan.`,
      }),
    );
    caja.appendChild(
      el("p", {
        clase: "pista",
        texto:
          "El archivo en disco no cambia hasta que exportes. Si te equivocas, " +
          "vuelve a abrir el .gramps sin guardar.",
      }),
    );
    const cancelar = el("button", { texto: "Cancelar" });
    cancelar.addEventListener("click", () => this.modal.cerrar());
    const borrar = el("button", { clase: "peligro", texto: "Borrar" });
    borrar.addEventListener("click", () => {
      borrarPersona(this.arbol, handle);
      this.seleccion = undefined;
      this.modal.cerrar();
      this.recargar();
    });
    caja.appendChild(el("div", { clase: "pie-modal", hijos: [cancelar, borrar] }));
    this.modal.abrir("Borrar persona", caja);
  }

  /**
   * Elige desde quien se lee el arbol. A partir de ahi, bajo cada nombre sale
   * el parentesco con esa persona.
   */
  private ponerPerspectiva(handle: string | undefined): void {
    this.perspectiva = handle;
    this.escena.ponerPerspectiva(handle);
    this.pintarPerspectiva();
    this.refrescar();
  }

  private pintarPerspectiva(): void {
    const etiqueta = buscar("#perspectiva-etiqueta");
    const quitar = buscar<HTMLButtonElement>("#btn-quitar-perspectiva");
    const p = this.perspectiva ? this.arbol.personas.get(this.perspectiva) : undefined;
    if (p) {
      etiqueta.textContent = `Desde ${nombreCompleto(p)}`;
      etiqueta.classList.remove("perspectiva-vacia");
      quitar.disabled = false;
    } else {
      etiqueta.textContent = "Sin punto de vista";
      etiqueta.classList.add("perspectiva-vacia");
      quitar.disabled = true;
    }
  }

  private seleccionar(handle: string): void {
    this.seleccion = handle;
    this.escena.ponerSeleccion([handle]);
    this.pestana = "ficha";
    this.activarPestana();
    this.refrescar();
  }

  private refrescar(): void {
    const ctx = this.contexto();
    pintarFicha(buscar('[data-panel="ficha"]'), ctx);
    pintarParentesco(buscar('[data-panel="parentesco"]'), ctx);
    pintarRevision(buscar('[data-panel="incidencias"]'), ctx);
    pintarResumen(buscar('[data-panel="resumen"]'), ctx);
    this.escena.ponerSeleccion(this.seleccion ? [this.seleccion] : []);
  }

  private pintarMetricas(m: Metricas): void {
    buscar("#metricas").textContent =
      `${m.fps.toFixed(0)} fps · ${m.nodos} personas · zoom ${m.escala.toFixed(2)}× · ` +
      `${m.fisicaViva ? "física activa" : "en reposo"} · ` +
      `${m.redibujosEnlaces} redibujos de enlaces/s`;
  }

  // --- barra y pestanas ---------------------------------------------------

  private conectarPestanas(): void {
    for (const b of document.querySelectorAll<HTMLButtonElement>("#pestanas button")) {
      b.addEventListener("click", () => {
        this.pestana = b.dataset["pestana"] ?? "ficha";
        this.activarPestana();
      });
    }
  }

  private activarPestana(): void {
    for (const b of document.querySelectorAll<HTMLElement>("#pestanas button")) {
      b.classList.toggle("activa", b.dataset["pestana"] === this.pestana);
    }
    for (const s of document.querySelectorAll<HTMLElement>("#contenido-panel > section")) {
      s.classList.toggle("activa", s.dataset["panel"] === this.pestana);
    }
  }

  private conectarBarra(): void {
    const archivo = buscar<HTMLInputElement>("#archivo-oculto");

    buscar("#btn-abrir").addEventListener("click", async () => {
      if (window.puente) {
        const r = await window.puente.abrir();
        if (r) this.cargarTexto(r.contenido, r.ruta);
      } else {
        archivo.click();
      }
    });
    archivo.addEventListener("change", async () => {
      const f = archivo.files?.[0];
      if (!f) return;
      this.cargarTexto(await f.text(), f.name);
      archivo.value = "";
    });

    buscar("#btn-guardar").addEventListener("click", () => {
      void this.exportar("gramps");
    });
    buscar("#btn-ged").addEventListener("click", () => {
      void this.exportar("ged");
    });

    buscar("#btn-encuadrar").addEventListener("click", () => this.escena.encuadrarTodo());
    buscar("#btn-quitar-perspectiva").addEventListener("click", () =>
      this.ponerPerspectiva(undefined),
    );
    buscar("#btn-nueva").addEventListener("click", () => this.contexto().nuevaPersona());

    // Zoom: el deslizador va en escala logaritmica, porque el rango util va
    // de 0.045 a 2.6 y en lineal todo el recorrido se gastaria en el final.
    const zoom = buscar<HTMLInputElement>("#zoom");
    zoom.addEventListener("input", () => {
      const t = Number(zoom.value) / 1000;
      const e = Math.exp(
        Math.log(ESCALA_MINIMA) + t * (Math.log(ESCALA_MAXIMA) - Math.log(ESCALA_MINIMA)),
      );
      this.escena.ponerEscala(e);
    });

    this.conectarBusqueda();
  }

  private conectarBusqueda(): void {
    const entrada = buscar<HTMLInputElement>("#buscar");
    const lista = buscar<HTMLElement>("#sugerencias");

    const cerrar = (): void => {
      lista.classList.add("oculto");
      vaciar(lista);
    };

    entrada.addEventListener("input", () => {
      const q = entrada.value.trim().toLowerCase();
      vaciar(lista);
      if (q.length < 2) {
        cerrar();
        return;
      }
      const hits = [...this.arbol.personas.entries()]
        .filter(([, p]) => nombreCompleto(p).toLowerCase().includes(q))
        .slice(0, 40);
      if (!hits.length) {
        cerrar();
        return;
      }
      for (const [h, p] of hits) {
        const b = el("button", { texto: etiquetaBusqueda(p) });
        b.addEventListener("click", () => {
          this.seleccionar(h);
          this.escena.irA(h);
          entrada.value = "";
          cerrar();
        });
        lista.appendChild(b);
      }
      lista.classList.remove("oculto");
    });

    entrada.addEventListener("blur", () => setTimeout(cerrar, 150));
    entrada.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") {
        entrada.value = "";
        cerrar();
      }
    });
  }

  private async exportar(formato: "gramps" | "ged"): Promise<void> {
    const base = this.nombreArchivo.replace(/\.(gramps|ged|xml)$/i, "") || "arbol";
    let contenido: string;
    let sugerencia: string;
    const avisos: string[] = [];

    if (formato === "gramps") {
      contenido = escribirGramps(this.arbol);
      sugerencia = `${base}.gramps`;
    } else {
      const r = escribirGedcom(this.arbol);
      contenido = r.texto;
      sugerencia = `${base}.ged`;
      avisos.push(
        `GEDCOM junta los dos apellidos en un solo campo: ${r.apellidosJuntados} ` +
          "persona(s) pierden el dato de qué rama aporta cada apellido. " +
          "También se pierden los tipos de unión distintos de «casados». " +
          "Para una copia completa, exporta a .gramps.",
      );
    }

    if (window.puente) {
      const ruta = await window.puente.guardar(sugerencia, contenido, formato);
      if (ruta) avisos.unshift(`Guardado en ${ruta}`);
    } else {
      const blob = new Blob([contenido], {
        type: formato === "gramps" ? "application/xml" : "text/plain",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = sugerencia;
      a.click();
      URL.revokeObjectURL(url);
    }
    if (avisos.length) this.mostrarAvisos("Exportación", avisos);
  }
}

const app = new Aplicacion();
void app.iniciar();

// Se expone para poder inspeccionar y cargar ficheros desde la consola del
// navegador sin pasar por el selector. No afecta a nada mas.
(window as unknown as { arbolApp: Aplicacion }).arbolApp = app;
